/**
 * Sem catálogo: produtos sem ficha no catálogo do Mercado Livre, preparados para anúncio próprio.
 * Categoria sugerida pelo preditor do ML (ou escolhida), atributos obrigatórios preenchidos com os dados do produto
 * e completados aqui, um a um ou em massa; depois, publicação pela mesma janela dos outros anúncios.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as Dialog from '@radix-ui/react-dialog';
import { Pencil, Search, Sparkles, X } from 'lucide-react';
import { api, int } from '../api.ts';
import { ErrorBox, Loading, Tag } from '../components/ui.tsx';
import { useMe } from '../components/AuthGate.tsx';
import { Head, useMeliAccount, useUrlState } from './MeliChannel.tsx';
import { PublishDialog } from './Publish.tsx';
import { can } from '@berytus/contracts';
import { AttrFilterButton } from '../components/AttrFilter.tsx';

type Status = 'no_category' | 'missing' | 'no_photo' | 'ready';
interface OwnItem {
  productId: string; sku: string; title: string | null; brand: string | null; gtin: string | null; photos: number;
  categoryId: string | null; categoryPath: string | null; categorySource: 'predicted' | 'manual' | null; predictError: string | null;
  status: Status; missing: string[];
  /** Fotos para o ML: ok, abaixo de 500 px, em conferência ou sem foto. */
  image: 'ok' | 'below_min' | 'unchecked' | 'none';
}
interface OwnList {
  total: number; page: number; pageSize: number;
  counts: Record<Status | 'all', number>;
  categories: { id: string; path: string | null; count: number }[];
  classes: { department: string | null; category: string | null; count: number }[];
  job: { id: string; status: string; stats: { checked?: number; predicted?: number; failed?: number } | null; createdAt: string } | null;
  items: OwnItem[];
}
interface AttrValue { value_id?: string; value_name?: string }
interface Field {
  id: string; name: string; required: boolean; valueType: string | null; options: { id: string; name: string }[] | null;
  value: AttrValue | null; source: 'manual' | 'auto' | null; autoText: string | null;
}
interface Draft {
  productId: string; sku: string; title: string | null; photos: number; gtin: string | null;
  categoryId: string | null; categoryPath: string | null; categorySource: string | null; predictError: string | null;
  fields: Field[]; missing: { id: string; name: string }[];
}
interface CategoryOption { id: string; name: string | null; path: string | null; domain: string | null }
interface CategoryAttr { id: string; name: string; value_type?: string; tags?: Record<string, boolean>; values?: { id: string; name: string }[] }

const STATUS: Record<Status, { label: string; tone: string }> = {
  no_category: { label: 'Sem categoria', tone: 'off' },
  missing: { label: 'Faltam atributos', tone: 'warn' },
  no_photo: { label: 'Sem foto', tone: 'warn' },
  ready: { label: 'Pronto', tone: 'ok' },
};
const closedList = (t: string | null | undefined) => t === 'list' || t === 'boolean';

export function OwnListingsPage() {
  const { account, isPending, error } = useMeliAccount();
  const admin = can(useMe().tenant.access, 'no_catalog', 'write');
  const qc = useQueryClient();
  const [params, set] = useUrlState();
  const status = (params.get('situacao') as Status | null) ?? 'all';
  const category = params.get('categoria') ?? '';
  const department = params.get('departamento') ?? '';
  const productCategory = params.get('cat') ?? '';
  const page = Number(params.get('page') ?? 1);
  const [q, setQ] = useState(params.get('q') ?? '');
  useEffect(() => {
    const t = setTimeout(() => set({ q: q.trim() || null, page: null }), 350);
    return () => clearTimeout(t);
  }, [q]); // eslint-disable-line react-hooks/exhaustive-deps

  const qs = new URLSearchParams({
    status, page: String(page), pageSize: '50', ...(category ? { category } : {}), ...(params.get('q') ? { q: params.get('q')! } : {}),
    ...(department ? { department } : {}), ...(productCategory ? { productCategory } : {}),
    ...(params.get('nunca') === '1' ? { never: '1' } : {}),
    ...(params.get('attrs') ? { attrs: params.get('attrs')! } : {}), ...(params.get('stock') === 'in' ? { stock: 'in' } : {}),
    ...(params.get('alert') ? { alert: params.get('alert')! } : {}),
  });
  const list = useQuery({
    queryKey: ['meli', 'own', account?.id, qs.toString()],
    queryFn: () => api<OwnList>(`/channels/accounts/${account!.id}/own?${qs}`),
    enabled: !!account,
    placeholderData: keepPreviousData,
    refetchInterval: (query) => (['queued', 'running'].includes(query.state.data?.job?.status ?? '') ? 3000 : false),
  });
  const prepare = useMutation({
    mutationFn: (force: boolean) => api(`/channels/accounts/${account!.id}/own/prepare`, { method: 'POST', json: { force } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['meli', 'own'] }),
  });
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [editing, setEditing] = useState<string | null>(null);
  const [bulk, setBulk] = useState<'attr' | 'category' | null>(null);
  const [publishing, setPublishing] = useState(false);
  const d = list.data;
  const items = d?.items ?? [];
  const job = d?.job;
  const running = job && ['queued', 'running'].includes(job.status);
  const selItems = items.filter((i) => selected.has(i.productId));
  const toggle = (id: string) => setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const allOnPage = items.length > 0 && items.every((i) => selected.has(i.productId));
  const refresh = () => qc.invalidateQueries({ queryKey: ['meli', 'own'] });

  return (
    <>
      <Head title="Sem catálogo">
        <p>Produtos sem ficha no catálogo do Mercado Livre. A Berytus sugere a categoria pelo título e preenche a ficha técnica com os dados do produto; o que faltar, você completa aqui (um a um ou em massa) e publica como anúncio próprio.</p>
      </Head>
      {(isPending || (account && list.isPending)) && <Loading />}
      {(error || list.error) && <ErrorBox error={error ?? list.error} />}
      {!isPending && !account && <div className="panel empty"><b>Nenhuma conta conectada</b>Conecte o Mercado Livre em Integração de canais.</div>}
      {d && (
        <div className="stack-y">
          <div className="seg" role="tablist" aria-label="Situação">
            {(['all', 'no_category', 'missing', 'no_photo', 'ready'] as const).map((k) => (
              <button key={k} type="button" aria-pressed={status === k} onClick={() => { set({ situacao: k === 'all' ? null : k, page: null }); setSelected(new Set()); }}>
                {k === 'all' ? 'Todos' : STATUS[k].label} <span className="muted">{int(d.counts[k])}</span>
              </button>
            ))}
          </div>

          <div className="toolbar">
            <label className="search">
              <Search size={16} />
              <span className="sr-only">Buscar</span>
              <input className="input" type="search" placeholder="Buscar por SKU, nome ou marca" value={q} onChange={(e) => setQ(e.target.value)} />
            </label>
            <ClassSelect classes={d.classes} department={department} category={productCategory}
              onChange={(dep, cat) => { set({ departamento: dep || null, cat: cat || null, page: null }); setSelected(new Set()); }} />
            <label className="small" style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }} title="Sem nenhum anúncio nesta conta (nem finalizado) e sem publicação em andamento">
              <input type="checkbox" className="check" checked={params.get('nunca') === '1'} onChange={(e) => { set({ nunca: e.target.checked ? '1' : null, page: null }); setSelected(new Set()); }} />
              Só nunca publicados
            </label>
            <AttrFilterButton params={params} set={(p) => { set(p); setSelected(new Set()); }} />
            <select className="select" aria-label="Alertas" value={params.get('alert') ?? ''} onChange={(e) => { set({ alert: e.target.value || null, page: null }); setSelected(new Set()); }}>
              <option value="">Alertas: todos</option>
              <option value="none">Sem alerta</option>
              <option value="any">Com algum alerta</option>
              <option value="same_sku">SKU já anunciado sem vínculo</option>
              <option value="failed">Última publicação recusada</option>
              <option value="price">Preço ou estoque (margem, custo)</option>
            </select>
            <label className="small" style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
              <input type="checkbox" className="check" checked={params.get('stock') === 'in'} onChange={(e) => { set({ stock: e.target.checked ? 'in' : null, page: null }); setSelected(new Set()); }} />
              Só com estoque
            </label>
            <select className="select" aria-label="Categoria do Mercado Livre" value={category} onChange={(e) => { set({ categoria: e.target.value || null, page: null }); setSelected(new Set()); }}>
              <option value="">Categoria ML: todas</option>
              {d.categories.map((c) => <option key={c.id} value={c.id}>{c.path ?? c.id} ({int(c.count)})</option>)}
            </select>
            {admin && (
              <button className="btn" type="button" disabled={!!running || prepare.isPending} onClick={() => prepare.mutate(false)}>
                <Sparkles size={15} /> {running ? `Sugerindo… ${int(job?.stats?.checked ?? 0)}` : 'Sugerir categorias'}
              </button>
            )}
          </div>
          {job && !running && job.stats && (
            <div className="small muted">
              Última sugestão: {int(job.stats.predicted ?? 0)} categoria(s) sugerida(s){job.stats.failed ? `, ${int(job.stats.failed)} sem sugestão (escolha à mão)` : ''}.
            </div>
          )}
          {prepare.error && <ErrorBox error={prepare.error} />}

          {selected.size > 0 && admin && (
            <div className="notice" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <b>{int(selected.size)} selecionado(s)</b>
              <button className="btn sm" type="button" onClick={() => setBulk('category')}>Trocar categoria</button>
              <button className="btn sm" type="button" onClick={() => setBulk('attr')}>Preencher atributo</button>
              <button className="btn sm primary" type="button" onClick={() => setPublishing(true)}>Anunciar selecionados</button>
              <button className="btn sm quiet" type="button" onClick={() => setSelected(new Set())}>Limpar</button>
            </div>
          )}

          {items.length === 0 ? (
            <div className="panel empty"><b>Nada por aqui</b>{d.counts.all === 0 ? 'Todos os produtos ativos têm ficha no catálogo ou já estão anunciados.' : 'Nenhum produto com esse filtro.'}</div>
          ) : (
            <div className="grid-wrap" style={{ opacity: list.isPlaceholderData ? 0.55 : 1 }}>
              <table className="grid" style={{ width: '100%', minWidth: 900 }}>
                <thead>
                  <tr>
                    <th style={{ width: 36 }}><input type="checkbox" className="check" aria-label="Selecionar a página" checked={allOnPage}
                      onChange={(e) => setSelected((s) => { const n = new Set(s); for (const i of items) { if (e.target.checked) n.add(i.productId); else n.delete(i.productId); } return n; })} /></th>
                    <th>Produto</th><th>Categoria no Mercado Livre</th><th>Falta</th><th>Situação</th><th style={{ width: 104 }} />
                  </tr>
                </thead>
                <tbody>
                  {items.map((i) => (
                    <tr key={i.productId}>
                      <td><input type="checkbox" className="check" aria-label={`Selecionar ${i.sku}`} checked={selected.has(i.productId)} onChange={() => toggle(i.productId)} /></td>
                      <td>
                        <div className="cell-2">
                          <Link to={`/catalogo/${i.productId}`} style={{ maxWidth: 340, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{i.title ?? 'Sem nome'}</Link>
                          <span className="sub">SKU {i.sku}{i.brand ? ` · ${i.brand}` : ''}{i.gtin ? '' : ' · sem EAN'}</span>
                        </div>
                      </td>
                      <td>
                        {i.categoryPath ? (
                          <div className="cell-2"><span style={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={i.categoryPath}>{i.categoryPath}</span>
                            <span className="sub">{i.categorySource === 'manual' ? 'escolhida' : 'sugerida'} · {i.categoryId}</span></div>
                        ) : <span className="small muted">{i.predictError ?? 'não sugerida ainda'}</span>}
                      </td>
                      <td className="small">{i.missing.length ? i.missing.join(', ') : i.status === 'no_photo' ? 'foto do produto' : '—'}</td>
                      <td><Tag tone={STATUS[i.status].tone}>{STATUS[i.status].label}</Tag>
                        {i.image === 'below_min' && <div className="small" style={{ color: 'var(--err)' }}>Imagem abaixo de 500 px</div>}
                        {i.image === 'unchecked' && i.photos > 0 && <div className="small muted">Foto em conferência para o ML</div>}</td>
                      <td>{admin && <button className="btn sm" type="button" onClick={() => setEditing(i.productId)}><Pencil size={13} /> Editar</button>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {d.total > d.pageSize && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <button className="btn sm" type="button" disabled={page <= 1} onClick={() => set({ page: String(page - 1) })}>Anterior</button>
              <span className="small muted">Página {page} de {Math.ceil(d.total / d.pageSize)} · {int(d.total)} produtos</span>
              <button className="btn sm" type="button" disabled={page * d.pageSize >= d.total} onClick={() => set({ page: String(page + 1) })}>Próxima</button>
            </div>
          )}
        </div>
      )}

      {editing && <EditDraft productId={editing} onClose={() => { setEditing(null); refresh(); }} />}
      {bulk === 'category' && <BulkCategory productIds={[...selected]} sample={selItems[0]?.title ?? ''} onClose={(done) => { setBulk(null); if (done) refresh(); }} />}
      {bulk === 'attr' && <BulkAttribute items={selItems} productIds={[...selected]} onClose={(done) => { setBulk(null); if (done) refresh(); }} />}
      {publishing && account && (
        <PublishDialog accountId={account.id} target={{ productIds: [...selected] }} onClose={() => setPublishing(false)} onDone={() => { setSelected(new Set()); refresh(); }} />
      )}
    </>
  );
}

function Modal({ title, onClose, children, width = 720 }: { title: string; onClose: () => void; children: React.ReactNode; width?: number }) {
  return (
    <Dialog.Root open onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="drawer-overlay" />
        <Dialog.Content className="panel" aria-describedby={undefined}
          style={{ position: 'fixed', top: '5vh', left: '50%', transform: 'translateX(-50%)', width: `min(${width}px, calc(100vw - 32px))`, maxHeight: '90vh', overflow: 'auto', zIndex: 60, display: 'grid', gap: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Dialog.Title asChild><h2 style={{ margin: 0 }}>{title}</h2></Dialog.Title>
            <span style={{ flex: 1 }} />
            <Dialog.Close className="icon-btn" aria-label="Fechar"><X size={18} /></Dialog.Close>
          </div>
          {children}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/** Busca de categoria pelo preditor do ML (a partir de um texto, por padrão o título do produto). */
function CategoryPicker({ initial, onPick, busy }: { initial: string; onPick: (c: CategoryOption) => void; busy?: boolean }) {
  const [text, setText] = useState(initial);
  const [term, setTerm] = useState(initial);
  const r = useQuery({
    queryKey: ['meli', 'predict', term],
    queryFn: () => api<CategoryOption[]>(`/meli/categories/predict?q=${encodeURIComponent(term)}`),
    enabled: term.trim().length >= 2,
  });
  return (
    <div className="stack-y" style={{ gap: 8 }}>
      <form style={{ display: 'flex', gap: 8 }} onSubmit={(e) => { e.preventDefault(); setTerm(text); }}>
        <input className="input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Descreva o produto (ex.: vitamina c efervescente)" style={{ flex: 1 }} />
        <button className="btn" type="submit"><Search size={14} /> Buscar</button>
      </form>
      {r.isFetching && <Loading />}
      {r.error && <ErrorBox error={r.error} />}
      {r.data && r.data.length === 0 && <span className="small muted">Nenhuma categoria sugerida. Tente outras palavras.</span>}
      {r.data?.map((c) => (
        <button key={c.id} type="button" className="btn" disabled={busy} style={{ justifyContent: 'flex-start', textAlign: 'left' }} onClick={() => onPick(c)}>
          <span><b>{c.name}</b><span className="small muted"> · {c.path} · {c.id}</span></span>
        </button>
      ))}
    </div>
  );
}

function FieldInput({ f, value, onChange }: { f: Field | { id: string; name: string; valueType?: string | null; options: { id: string; name: string }[] | null }; value: AttrValue | null; onChange: (v: AttrValue | null) => void }) {
  const opts = f.options ?? [];
  if (opts.length && closedList(f.valueType)) {
    return (
      <select className="select" value={value?.value_id ?? ''} onChange={(e) => {
        const o = opts.find((x) => x.id === e.target.value);
        onChange(o ? { value_id: o.id, value_name: o.name } : null);
      }}>
        <option value="">Escolha…</option>
        {opts.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
      </select>
    );
  }
  const listId = `opts-${f.id}`;
  return (
    <>
      <input className="input" list={opts.length ? listId : undefined} value={value?.value_name ?? ''}
        placeholder={f.valueType === 'number_unit' ? 'ex.: 120 g, 15 cm, 500 ml' : ''}
        onChange={(e) => {
          const t = e.target.value;
          const o = opts.find((x) => x.name.toLowerCase() === t.trim().toLowerCase());
          onChange(t.trim() ? (o ? { value_id: o.id, value_name: o.name } : { value_name: t }) : null);
        }} />
      {opts.length > 0 && <datalist id={listId}>{opts.slice(0, 200).map((o) => <option key={o.id} value={o.name} />)}</datalist>}
    </>
  );
}

function EditDraft({ productId, onClose }: { productId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['meli', 'draft', productId], queryFn: () => api<Draft>(`/catalog/products/${productId}/meli-draft`) });
  const [values, setValues] = useState<Record<string, AttrValue | null>>({});
  const [picking, setPicking] = useState(false);
  const save = useMutation({
    mutationFn: (body: { categoryId?: string; attributes?: Record<string, AttrValue | null> }) =>
      api<Draft>(`/catalog/products/${productId}/meli-draft`, { method: 'PUT', json: body }),
    onSuccess: (d) => { qc.setQueryData(['meli', 'draft', productId], d); setValues({}); setPicking(false); },
  });
  const d = q.data;
  const dirty = Object.keys(values).length > 0;
  return (
    <Modal title="Anúncio próprio" onClose={onClose}>
      {q.isPending ? <Loading /> : q.error ? <ErrorBox error={q.error} /> : d && (
        <>
          <div>
            <b>{d.title}</b>
            <div className="small muted">SKU {d.sku}{d.gtin ? ` · EAN ${d.gtin}` : ' · sem EAN'} · {d.photos} foto(s){d.photos === 0 && <> — <Link to={`/catalogo/${d.productId}`}>adicionar fotos</Link></>}</div>
          </div>
          <section className="stack-y" style={{ gap: 8 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <b>Categoria:</b>
              <span>{d.categoryPath ?? <span className="muted">não definida</span>}</span>
              {d.categoryId && <span className="small muted">({d.categorySource === 'manual' ? 'escolhida' : 'sugerida'} · {d.categoryId})</span>}
              <button className="btn sm" type="button" onClick={() => setPicking((p) => !p)}>{picking ? 'Cancelar' : d.categoryId ? 'Trocar' : 'Escolher'}</button>
            </div>
            {picking && <CategoryPicker initial={d.title ?? ''} busy={save.isPending} onPick={(c) => save.mutate({ categoryId: c.id })} />}
          </section>
          {d.categoryId && (
            <section className="stack-y" style={{ gap: 10 }}>
              <div className="small muted">
                Obrigatórios primeiro. <Tag tone="info">auto</Tag> veio dos dados do produto; o que você digitar vale só para o Mercado Livre e vence o automático.
              </div>
              {d.fields.map((f) => {
                const v = f.id in values ? values[f.id]! : f.value;
                return (
                  <div key={f.id} className="field">
                    <label>
                      {f.name} {f.required && <span style={{ color: 'var(--err)' }}>*</span>}{' '}
                      {f.source === 'auto' && !(f.id in values) && <Tag tone="info">auto</Tag>}
                      {f.autoText && !v && <span className="small muted"> · "{f.autoText}" não está na lista do Mercado Livre</span>}
                    </label>
                    <FieldInput f={f} value={v} onChange={(nv) => setValues((s) => ({ ...s, [f.id]: nv }))} />
                  </div>
                );
              })}
              {d.missing.length > 0 && <div className="notice small">Falta: {d.missing.map((m) => m.name).join(', ')}</div>}
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn primary" type="button" disabled={!dirty || save.isPending} onClick={() => save.mutate({ attributes: values })}>{save.isPending ? 'Salvando…' : 'Salvar'}</button>
                <button className="btn" type="button" onClick={onClose}>Fechar</button>
              </div>
            </section>
          )}
          {save.error && <ErrorBox error={save.error} />}
        </>
      )}
    </Modal>
  );
}

function BulkCategory({ productIds, sample, onClose }: { productIds: string[]; sample: string; onClose: (done: boolean) => void }) {
  const save = useMutation({
    mutationFn: (categoryId: string) => api('/channels/own/bulk', { method: 'POST', json: { productIds, categoryId } }),
    onSuccess: () => onClose(true),
  });
  return (
    <Modal title={`Categoria para ${int(productIds.length)} produto(s)`} onClose={() => onClose(false)} width={640}>
      <CategoryPicker initial={sample} busy={save.isPending} onPick={(c) => save.mutate(c.id)} />
      {save.error && <ErrorBox error={save.error} />}
    </Modal>
  );
}

function BulkAttribute({ items, productIds, onClose }: { items: OwnItem[]; productIds: string[]; onClose: (done: boolean) => void }) {
  const cats = [...new Set(items.map((i) => i.categoryId).filter(Boolean))] as string[];
  const cat = cats.length === 1 ? cats[0]! : null;
  const c = useQuery({ queryKey: ['meli', 'category', cat], queryFn: () => api<{ attributes: CategoryAttr[] }>(`/meli/categories/${cat}`), enabled: !!cat });
  const attrs = useMemo(() => (c.data?.attributes ?? []).filter((a) => !a.tags?.read_only && !a.tags?.hidden && !['SELLER_SKU', 'ITEM_CONDITION', 'GTIN', 'EMPTY_GTIN_REASON'].includes(a.id))
    .sort((a, b) => Number(!!(b.tags?.required || b.tags?.catalog_required)) - Number(!!(a.tags?.required || a.tags?.catalog_required)) || a.name.localeCompare(b.name)), [c.data]);
  const [attrId, setAttrId] = useState('');
  const [value, setValue] = useState<AttrValue | null>(null);
  const a = attrs.find((x) => x.id === attrId);
  const save = useMutation({
    mutationFn: () => api('/channels/own/bulk', { method: 'POST', json: { productIds, attributes: { [attrId]: value } } }),
    onSuccess: () => onClose(true),
  });
  return (
    <Modal title={`Preencher atributo em ${int(productIds.length)} produto(s)`} onClose={() => onClose(false)} width={560}>
      {!cat ? (
        <div className="notice small">Os selecionados estão em categorias diferentes (ou sem categoria). Filtre por uma categoria e selecione de novo.</div>
      ) : c.isPending ? <Loading /> : c.error ? <ErrorBox error={c.error} /> : (
        <>
          <div className="field">
            <label>Atributo</label>
            <select className="select" value={attrId} onChange={(e) => { setAttrId(e.target.value); setValue(null); }}>
              <option value="">Escolha…</option>
              {attrs.map((x) => <option key={x.id} value={x.id}>{x.name}{x.tags?.required || x.tags?.catalog_required ? ' *' : ''}</option>)}
            </select>
          </div>
          {a && (
            <div className="field">
              <label>Valor</label>
              <FieldInput f={{ id: a.id, name: a.name, valueType: a.value_type ?? null, options: a.values ?? null }} value={value} onChange={setValue} />
            </div>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn primary" type="button" disabled={!a || !value || save.isPending} onClick={() => save.mutate()}>{save.isPending ? 'Aplicando…' : `Aplicar em ${int(productIds.length)}`}</button>
            <button className="btn" type="button" onClick={() => onClose(false)}>Cancelar</button>
          </div>
        </>
      )}
      {save.error && <ErrorBox error={save.error} />}
    </Modal>
  );
}

/** Departamento e categoria do cadastro do cliente, em cascata, com quantos produtos há em cada um (também na Shopee). */
type ClassRow = { department: string | null; category: string | null; count: number };
export function ClassSelect({ classes, department, category, onChange }: {
  classes: ClassRow[]; department: string; category: string; onChange: (department: string, category: string) => void;
}) {
  const count = (rows: ClassRow[]) => rows.reduce((a, r) => a + r.count, 0);
  const departments = [...new Set(classes.map((c) => c.department).filter((v): v is string => !!v))];
  const inDep = classes.filter((c) => !department || c.department === department);
  const categories = [...new Set(inDep.map((c) => c.category).filter((v): v is string => !!v))].sort((a, b) => a.localeCompare(b, 'pt-BR'));
  return (
    <>
      {departments.length > 0 && (
        <select className="select" style={{ maxWidth: 220 }} aria-label="Departamento do cadastro" value={department} onChange={(e) => onChange(e.target.value, '')}>
          <option value="">Departamento: todos</option>
          {departments.map((v) => <option key={v} value={v}>{v} · {int(count(classes.filter((c) => c.department === v)))}</option>)}
          {department && !departments.includes(department) && <option value={department}>{department}</option>}
        </select>
      )}
      {categories.length > 0 && (
        <select className="select" style={{ maxWidth: 240 }} aria-label="Categoria do cadastro" value={category} onChange={(e) => onChange(department, e.target.value)}>
          <option value="">{department ? `Categoria: todas de ${department}` : 'Categoria: todas'}</option>
          {categories.map((v) => <option key={v} value={v}>{v} · {int(count(inDep.filter((c) => c.category === v)))}</option>)}
          {category && !categories.includes(category) && <option value={category}>{category}</option>}
        </select>
      )}
    </>
  );
}
